import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from 'urql'
import {
  GET_PORTAL_HEALTH,
  GET_PORTAL_HEALTH_SURFACE,
} from '../../graphql/portalHealth'
import { k8sStatusLabel } from '../../utils/k8sStatusLabel'
import { vultrHeadline } from '../../utils/vultrStatusLabel'
import { getPortalCardHelp } from '../../utils/portalIndicatorHelp'
import { getServiceLogLinks, resolveLogJob } from '../../utils/portalLogCatalog'
import PortalUptimeChart from './PortalUptimeChart'
import FleetDrilldown from './FleetDrilldown'
import './PortalHealth.css'

const POLL_MS = 60000
/** Inventario completo (Vultr/Atlas/colas/tráfico); el backend cachea collect ~90s. */
const FULL_POLL_MS = 120000
const FLEET_COLS = 11

/** Deep-link al Management UI de una cola (vhost `/` → %2F). */
function buildRabbitQueueUrl(name, vhost = '/') {
  const vh = encodeURIComponent(vhost || '/')
  return `https://rabbit.smartcfo.cl/#/queues/${vh}/${encodeURIComponent(name)}`
}

/** Áreas del portal: orden por criticidad operativa. */
const TABS = [
  {
    id: 'resumen',
    label: 'Resumen',
    hint: 'Checklist matutino, probes del portal 24h y tráfico pesado (usuarios / pods).',
  },
  {
    id: 'rabbit',
    label: 'Rabbit',
    hint: 'Broker rabbitmq-ha: API, memoria de nodos Erlang y colas clave.',
    indicatorIds: ['rabbit_mgmt', 'rabbit_memory', 'rabbit_queues'],
    fleetNames: ['rabbitmq-ha'],
    autoExpand: true,
    panel: 'rabbit',
  },
  {
    id: 'nodes',
    label: 'Nodos',
    hint: 'Nodos EKS (AWS): Ready, MemoryPressure, CPU/Mem vs allocatable.',
    indicatorIds: ['k8s_nodes'],
    panel: 'nodes',
  },
  {
    id: 'vultr',
    label: 'Vultr',
    hint: 'Instances de la cuenta: status, power_status y server_status (List Instances).',
    indicatorIds: ['vultr_ops'],
    panel: 'vultr',
  },
  {
    id: 'mongo',
    label: 'Mongo',
    hint: 'Memoria de host Atlas (System Memory) del cluster main — misma señal que las alertas de RAM.',
    indicatorIds: ['mongo_memory'],
    panel: 'mongo',
  },
  {
    id: 'server',
    label: 'Server',
    hint: 'Readiness de smart_back y deployment smart-tech-api.',
    indicatorIds: ['smart_back_ready'],
    fleetNames: ['smart-tech-api'],
    autoExpand: true,
  },
  {
    id: 'front',
    label: 'Front',
    hint: 'SPA CloudFront: shell /signin, assets JS/CSS y GraphQL del portal.',
    indicatorIds: [
      'portal_front_signin',
      'portal_front_assets',
      'portal_front_graphql',
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    hint: 'document-worker y colas create-document / tax-documents / bsale.',
    fleetNames: ['document-worker'],
    autoExpand: true,
    relatedQueues: [
      'tax-documents-queue',
      'create-document-queue',
      'create-document-cron-queue',
      'create-document-cedidos-queue',
      'create-document-cedidos-cron-queue',
      'create-sii-resume-queue',
      'create-bsale-queue',
      'create-bsale-cron-queue',
    ],
  },
  {
    id: 'mailing',
    label: 'Mailing',
    hint: 'Worker mailing y colas email / email-priority.',
    fleetNames: ['mailing'],
    autoExpand: true,
    relatedQueues: ['email-queue', 'email-priority-queue'],
  },
  {
    id: 'sii',
    label: 'SII',
    hint: 'sii-worker y colas sii-* / tax-documents.',
    fleetNames: ['sii-worker'],
    autoExpand: true,
    relatedQueues: [
      'sii-queue',
      'sii-priority-queue',
      'sii-resume-queue',
      'tax-documents-queue',
    ],
  },
  {
    id: 'api',
    label: 'API pública',
    hint: 'api.smartcfo.cl y public-api-worker.',
    indicatorIds: ['public_api'],
    fleetNames: ['public-api-worker'],
    autoExpand: true,
  },
  {
    id: 'mcp',
    label: 'MCP',
    hint: 'Cadena MCP → API → smart_back y OAuth del connector.',
    indicatorIds: [
      'mcp_health',
      'oauth_discovery',
      'oauth_login',
      'public_api',
      'public_api_deps',
      'smart_back_ready',
      'mcp_traffic',
    ],
    fleetNames: ['portal-mcp-worker', 'public-api-worker'],
    autoExpand: true,
  },
  {
    id: 'admin',
    label: 'Admin',
    hint: 'server-admin-api en namespace admin-panel.',
    fleetNames: ['server-admin-api'],
    autoExpand: true,
  },
]

const TABS_BY_ID = Object.fromEntries(TABS.map((t) => [t.id, t]))

/** Barra: capas, no criticidad. El click sigue abriendo la pestaña. */
const TAB_GROUPS = [
  { id: 'overview', label: null, tabIds: ['resumen'] },
  { id: 'portal', label: 'Portal', tabIds: ['front', 'server', 'admin'] },
  { id: 'datos', label: 'Datos', tabIds: ['mongo', 'rabbit'] },
  { id: 'computo', label: 'Cómputo', tabIds: ['nodes', 'vultr'] },
  { id: 'workers', label: 'Workers', tabIds: ['documents', 'sii', 'mailing'] },
  { id: 'afuera', label: 'Afuera', tabIds: ['api', 'mcp'] },
]

/** Bloques del checklist matutino (Resumen). */
const CHECKLIST_BLOCKS = [
  {
    id: 'entrada',
    label: 'Entrada portal',
    tab: 'front',
    indicatorIds: [
      'portal_front_signin',
      'portal_front_assets',
      'portal_front_graphql',
    ],
    logJob: 'smart-tech-api',
  },
  {
    id: 'server',
    label: 'Server',
    tab: 'server',
    indicatorIds: ['smart_back_ready'],
    fleetNames: ['smart-tech-api'],
    logJob: 'smart-tech-api',
  },
  {
    id: 'mongo',
    label: 'Mongo',
    tab: 'mongo',
    indicatorIds: ['mongo_memory'],
  },
  {
    id: 'rabbit_broker',
    label: 'Rabbit broker',
    tab: 'rabbit',
    indicatorIds: ['rabbit_mgmt', 'rabbit_memory', 'rabbit_ui'],
    fleetNames: ['rabbitmq-ha'],
    // Sin scrape Loki para rabbitmq-ha — el detalle está en pestaña Rabbit / Management
    logJob: null,
  },
  {
    id: 'rabbit_queues',
    label: 'Colas críticas',
    tab: 'rabbit',
    indicatorIds: ['rabbit_queues'],
    // Cada cola resuelve su worker vía resolveLogJob
    logJob: null,
  },
  {
    id: 'nodes',
    label: 'Nodos EKS',
    tab: 'nodes',
    indicatorIds: ['k8s_nodes'],
  },
  {
    id: 'vultr',
    label: 'Vultr',
    tab: 'vultr',
    indicatorIds: ['vultr_ops'],
  },
  {
    id: 'fleet',
    label: 'Flota crítica',
    tab: 'resumen',
    indicatorIds: ['k8s_fleet'],
    fleetCritical: true,
  },
]

function morningLabel(status) {
  switch (status) {
    case 'ok':
      return 'OK'
    case 'warn':
      return 'Atención'
    case 'error':
      return 'Bloqueante'
    default:
      return 'Unknown'
  }
}

function morningGoLabel(status) {
  switch (status) {
    case 'ok':
      return 'Listo'
    case 'warn':
      return 'Atención'
    case 'error':
      return 'Bloqueado'
    default:
      return 'Unknown'
  }
}

function LogLinks({ job, compact }) {
  const links = getServiceLogLinks(job)
  if (!links) return null
  return (
    <a
      className={`ph-btn ph-btn-link${compact ? ' ph-btn-link--compact' : ''}`}
      href={links.url || links.allUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={links.hint || `Loki · {job="${links.job}"}`}
      onClick={(e) => e.stopPropagation()}
    >
      Logs
    </a>
  )
}

function buildChecklistRows(byId, fleet, rabbitQueues, nodes, vultrInstances) {
  const criticalFleet = fleet.filter((f) => f.tier === 'critical')
  return CHECKLIST_BLOCKS.map((block) => {
    const statuses = []
    /** @type {{ key: string, label: string, status: string, summary: string, detail?: string, logJob?: string }[]} */
    const items = []

    for (const id of block.indicatorIds || []) {
      const ind = byId[id]
      if (!ind?.status) continue
      statuses.push(ind.status)
      // Desglose propio (colas/nodos/vultr/flota): no repetir el indicador agregado
      const hasBreakdown =
        block.id === 'rabbit_queues' ||
        block.id === 'nodes' ||
        block.id === 'vultr' ||
        block.fleetCritical
      if (ind.status !== 'ok' && !hasBreakdown) {
        items.push({
          key: `ind-${id}`,
          label: ind.label || id,
          status: ind.status,
          summary: ind.summary || '—',
          detail: ind.detail || undefined,
          logJob: block.logJob,
        })
      }
    }
    if (block.fleetNames?.length) {
      for (const name of block.fleetNames) {
        const row = fleet.find((f) => f.name === name)
        if (!row?.status) continue
        statuses.push(row.status)
        if (row.status !== 'ok') {
          items.push({
            key: `fleet-${row.namespace}-${row.name}`,
            label: row.name,
            status: row.status,
            summary:
              row.reason ||
              `${row.ready}/${row.desired} ready` ||
              row.status,
            detail: [
              row.namespace,
              row.crashLoopCount ? `CrashLoop ${row.crashLoopCount}` : null,
              row.oomCount ? `OOM ${row.oomCount}` : null,
              row.memPct != null ? `mem ${row.memPct}%` : null,
            ]
              .filter(Boolean)
              .join(' · '),
            logJob: resolveLogJob(name),
          })
        }
      }
    }
    if (block.fleetCritical) {
      for (const row of criticalFleet) {
        if (row.status && row.status !== 'ok') {
          statuses.push(row.status)
          items.push({
            key: `crit-${row.namespace}-${row.name}`,
            label: row.name,
            status: row.status,
            summary: row.reason || `${row.ready}/${row.desired} ready`,
            detail: row.namespace,
            logJob: resolveLogJob(row.name),
          })
        }
      }
      if (!statuses.length) statuses.push('ok')
    }
    if (block.id === 'rabbit_queues' && Array.isArray(rabbitQueues)) {
      for (const q of rabbitQueues.filter((x) => x.status !== 'ok')) {
        statuses.push(q.status)
        items.push({
          key: `q-${q.name}`,
          label: q.name,
          status: q.status,
          summary: `${q.ready} ready · ${q.consumers} consumers · unacked ${q.unacked}`,
          detail: q.state || undefined,
          logJob: resolveLogJob(q.name),
        })
      }
    }
    if (block.id === 'nodes' && Array.isArray(nodes)) {
      for (const n of nodes.filter((x) => x.status !== 'ok')) {
        statuses.push(n.status)
        const short = String(n.name || '').split('.')[0] || n.name
        items.push({
          key: `node-${n.name}`,
          label: short,
          status: n.status,
          summary:
            n.reason ||
            [
              n.ready ? 'Ready' : 'NotReady',
              n.cpuPct != null ? `CPU ${n.cpuPct}%` : null,
              n.memPct != null ? `Mem ${n.memPct}%` : null,
            ]
              .filter(Boolean)
              .join(' · '),
          detail: [n.instanceType, n.zone].filter(Boolean).join(' · '),
        })
      }
    }
    if (block.id === 'vultr' && Array.isArray(vultrInstances)) {
      for (const t of vultrInstances.filter((x) => x.status !== 'ok')) {
        statuses.push(t.status)
        items.push({
          key: `vultr-${t.id}`,
          label: t.label || t.id,
          status: t.status,
          summary:
            t.summary ||
            `status ${t.instanceStatus} · power_status ${t.powerStatus} · server_status ${t.serverStatus}`,
          detail: [t.mainIp, t.region, t.plan, t.uso].filter(Boolean).join(' · '),
        })
      }
    }

    const status = statuses.length ? worstStatus(statuses) : 'unknown'
    const detail =
      items.length > 0
        ? items.map((i) => `${i.label}: ${i.summary}`).join(' · ')
        : status === 'ok'
          ? 'Sin alertas'
          : '—'

    return {
      ...block,
      status,
      detail,
      items,
    }
  })
}

function AffectedItemsPanel({ rows, onOpenTab }) {
  const affected = []
  for (const row of rows || []) {
    if (row.status === 'ok' || row.status === 'unknown') continue
    for (const item of row.items || []) {
      affected.push({
        ...item,
        blockLabel: row.label,
        blockTab: row.tab,
        blockLogJob: row.logJob,
      })
    }
    if (!row.items?.length) {
      affected.push({
        key: `block-${row.id}`,
        label: row.label,
        status: row.status,
        summary: row.detail || morningLabel(row.status),
        blockLabel: row.label,
        blockTab: row.tab,
        blockLogJob: row.logJob,
        logJob: row.logJob,
      })
    }
  }
  affected.sort(
    (a, b) => (STATUS_RANK[b.status] ?? 0) - (STATUS_RANK[a.status] ?? 0),
  )

  if (affected.length === 0) return null

  const blockers = affected.filter((a) => a.status === 'error').length
  const attention = affected.filter((a) => a.status === 'warn').length

  return (
    <div className="ph-affected" role="region" aria-label="Elementos afectados">
      <header className="ph-affected-head">
        <div>
          <h3 className="ph-affected-title">Requiere acción</h3>
          <p className="ph-affected-sub">
            {blockers > 0
              ? `${blockers} bloqueante${blockers === 1 ? '' : 's'}`
              : null}
            {blockers > 0 && attention > 0 ? ' · ' : null}
            {attention > 0 ? `${attention} en atención` : null}
            {!blockers && !attention ? `${affected.length} ítems` : null}
          </p>
        </div>
      </header>
      <ul className="ph-affected-list">
        {affected.map((item) => (
          <li
            key={item.key}
            className={`ph-affected-item ph-affected-item--${item.status}`}
          >
            <div className={`ph-affected-rail ph-affected-rail--${item.status}`} />
            <div className="ph-affected-body">
              <div className="ph-affected-item-title">
                <span
                  className={`ph-sev ph-sev--${item.status}`}
                >
                  {morningLabel(item.status)}
                </span>
                <strong className="ph-affected-name">{item.label}</strong>
                <span className="ph-affected-block">{item.blockLabel}</span>
              </div>
              <p className="ph-affected-summary">{item.summary}</p>
              {item.detail ? (
                <p className="ph-affected-detail">{item.detail}</p>
              ) : null}
            </div>
            <div className="ph-affected-item-actions">
              {item.blockTab && item.blockTab !== 'resumen' ? (
                <button
                  type="button"
                  className="ph-btn ph-btn--primary"
                  onClick={() => onOpenTab(item.blockTab)}
                >
                  Revisar
                </button>
              ) : null}
              {item.logJob ? <LogLinks job={item.logJob} compact /> : null}
              {!item.logJob && item.blockTab === 'rabbit' ? (
                <a
                  className="ph-btn ph-btn-link"
                  href="https://rabbit.smartcfo.cl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Management
                </a>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function BlockStatusGrid({ rows, onOpenTab, fetching }) {
  if (!fetching && (!rows || rows.length === 0)) {
    return <p className="ph-empty">Sin datos del checklist</p>
  }
  return (
    <div className="ph-blocks-grid" role="list">
      {(rows || []).map((row) => (
        <button
          key={row.id}
          type="button"
          role="listitem"
          className={`ph-block-tile ph-block-tile--${row.status}`}
          onClick={() => onOpenTab(row.tab)}
          title={row.detail}
        >
          <span className={`ph-block-tile-bar ph-block-tile-bar--${row.status}`} />
          <span className="ph-block-tile-label">{row.label}</span>
          <span className={`ph-block-tile-status ph-block-tile-status--${row.status}`}>
            {morningLabel(row.status)}
          </span>
          {row.status !== 'ok' && row.items?.length ? (
            <span className="ph-block-tile-meta">
              {row.items.length} ítem{row.items.length === 1 ? '' : 's'}
            </span>
          ) : (
            <span className="ph-block-tile-meta ph-block-tile-meta--ok">
              {row.status === 'ok' ? 'Sin alertas' : '—'}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

/** Overlay de probes de superficie (ready/shell/GraphQL) sobre el inventario. */
function mergeSurfaceIndicators(fullIndicators, surfaceIndicators) {
  if (!surfaceIndicators?.length) return fullIndicators
  const byId = Object.fromEntries(
    (fullIndicators || []).map((i) => [i.id, i]),
  )
  for (const ind of surfaceIndicators) {
    byId[ind.id] = ind
  }
  return Object.values(byId)
}

function ResumenHero({
  overall,
  blocking,
  attention,
  uptime,
  checkedAt,
  loading,
}) {
  const upPct = uptime?.uptimePct
  const downMin = uptime?.downtimeMinutes
  const hasOverall =
    overall === 'ok' ||
    overall === 'warn' ||
    overall === 'error' ||
    overall === 'unknown'
  // "Consultando…" solo sin dato aún; unknown es un estado real (no spinner).
  const line = !hasOverall && loading
    ? 'Consultando…'
    : overall === 'ok'
      ? 'Listo para operar'
      : overall === 'warn'
        ? `${attention} en atención${
            blocking ? ` · ${blocking} bloqueante${blocking === 1 ? '' : 's'}` : ''
          }`
        : overall === 'error'
          ? `${blocking} bloqueante${blocking === 1 ? '' : 's'}${
              attention ? ` · ${attention} en atención` : ''
            }`
          : overall === 'unknown'
            ? 'Estado incompleto · revisar checklist'
            : loading
              ? 'Consultando…'
              : 'Sin datos'
  return (
    <div className={`ph-hero ph-hero--${overall || 'unknown'}`} role="status">
      <div className="ph-hero-main">
        <p className="ph-hero-kicker">Estado del portal</p>
        <div className="ph-hero-status-row">
          <StatusBadge
            status={overall || 'unknown'}
            label={
              !hasOverall && loading ? 'Consultando' : morningGoLabel(overall)
            }
          />
          <p className="ph-hero-line">{line}</p>
        </div>
        <p className="ph-hero-checked">
          Actualizado {formatTime(checkedAt)}
        </p>
      </div>
      <div className="ph-hero-uptime">
        <p className="ph-hero-kicker">Probes portal 24h</p>
        <p className="ph-hero-uptime-pct">
          {upPct != null && Number.isFinite(Number(upPct))
            ? `${Number(upPct).toFixed(1)}%`
            : '—'}
        </p>
        <p className="ph-hero-uptime-meta">
          {downMin != null && downMin > 0
            ? `${downMin < 60 ? `${downMin} min` : `${Math.round(downMin / 60)}h`} downtime`
            : (uptime?.points || []).some((p) => (p.downSamples || 0) > 0)
              ? 'Hubo caídas en el gráfico (ver franja)'
              : 'Sin downtime registrado'}
        </p>
      </div>
    </div>
  )
}

const STATUS_RANK = { ok: 0, unknown: 1, warn: 2, error: 3 }

function worstStatus(statuses) {
  return statuses.reduce((acc, s) => {
    const a = STATUS_RANK[acc] ?? 0
    const b = STATUS_RANK[s] ?? 0
    return b > a ? s : acc
  }, 'ok')
}

function StatusDot({ status }) {
  return <span className={`ph-dot ph-dot--${status || 'unknown'}`} aria-hidden />
}

function StatusBadge({ status, label }) {
  return (
    <span className={`ph-badge ph-badge--${status || 'unknown'}`}>
      <StatusDot status={status} />
      {label || k8sStatusLabel(status)}
    </span>
  )
}

/** Hint colapsado: círculo “i” — tip al hover / focus. */
function InfoTip({ children, text }) {
  const body = children || text
  if (!body) return null

  return (
    <span className="ph-info">
      <button
        type="button"
        className="ph-info-btn"
        aria-label="Más información"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        i
      </button>
      <span className="ph-info-pop" role="tooltip">
        {body}
      </span>
    </span>
  )
}

function SectionTitle({ children, info, as: Tag = 'h2', className = '' }) {
  return (
    <Tag className={`ph-section-title ${className}`.trim()}>
      <span className="ph-section-title-row">
        <span className="ph-section-title-text">{children}</span>
        {info ? <InfoTip text={info} /> : null}
      </span>
    </Tag>
  )
}

/** Labels Rabbit (no nomenclatura K8s Ready/Unavailable). */
function rabbitQueueStatusLabel(q) {
  if (q.state === 'missing') return 'Ausente'
  if (q.consumeMode === 'get') {
    return 'OK'
  }
  if (q.status === 'error') {
    if ((q.ready || 0) > 0 && (q.consumers || 0) === 0) return 'Sin consumer'
    return 'Saturada'
  }
  if (q.status === 'warn') {
    if ((q.ready || 0) > 0 && (q.consumers || 0) === 0) return 'Polling (get)'
    return 'Backlog'
  }
  if (q.status === 'unknown') return 'Sin datos'
  return 'OK'
}

const GET_POLL_TIP =
  'Excepción SII: el worker usa channel.get() (polling), no basic.consume. Por eso consumers = 0 en Management aunque la cola se procese. Es el diseño esperado.'

function ConsumeModeTag({ mode }) {
  if (mode !== 'get') return null
  return (
    <span className="ph-mode-tag" title={GET_POLL_TIP}>
      get / poll
    </span>
  )
}

const QUEUE_STATUS_RANK = { error: 0, warn: 1, unknown: 2, ok: 3 }

/** Orden de criticidad en tabla: problemas primero, luego catálogo. */
function sortQueuesByCriticality(rows) {
  return [...rows].sort((a, b) => {
    const ra = QUEUE_STATUS_RANK[a.status] ?? 9
    const rb = QUEUE_STATUS_RANK[b.status] ?? 9
    if (ra !== rb) return ra - rb
    return String(a.name).localeCompare(String(b.name))
  })
}

function formatPct(v) {
  if (v == null || Number.isNaN(v)) return '—'
  return `${v}%`
}

function formatRate(v) {
  if (v == null || Number.isNaN(v)) return '—'
  const n = Number(v)
  if (n === 0) return '0.00/s'
  if (Math.abs(n) < 0.01) return `${n.toFixed(4)}/s`
  return `${n.toFixed(2)}/s`
}

function formatCount(v) {
  if (v == null || Number.isNaN(v)) return '—'
  return Number(v).toLocaleString('es-CL')
}

function shortNode(node) {
  if (!node) return '—'
  const m = String(node).match(/rabbitmq-ha-\d+/)
  return m ? m[0] : node.replace(/\.rabbitmq-ha-headless.*$/, '')
}

function formatTime(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return '—'
  }
}

function IndicatorCard({ ind, open, onToggle, statusLabel }) {
  const cardRef = useRef(null)
  const popRef = useRef(null)
  const [pos, setPos] = useState(null)
  const help = useMemo(
    () => getPortalCardHelp(ind, statusLabel),
    [ind, statusLabel],
  )
  const chip =
    ind.id === 'vultr_ops'
      ? statusLabel || 'unknown'
      : statusLabel || k8sStatusLabel(ind.status)

  const placePopover = useCallback(() => {
    const el = cardRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const width = Math.min(Math.max(r.width, 300), 420)
    const gap = 8
    const popH = popRef.current?.offsetHeight || 320
    const spaceBelow = window.innerHeight - r.bottom - gap
    const spaceAbove = r.top - gap
    const placeAbove = spaceBelow < popH && spaceAbove > spaceBelow
    let left = r.left
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12)
    }
    setPos({
      left,
      width,
      top: placeAbove ? undefined : r.bottom + gap,
      bottom: placeAbove ? window.innerHeight - r.top + gap : undefined,
    })
  }, [])

  useEffect(() => {
    if (!open) {
      setPos(null)
      return undefined
    }
    placePopover()
    // Re-measure after paint (altura real del popover).
    const t = window.requestAnimationFrame(placePopover)
    const onScroll = () => placePopover()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      window.cancelAnimationFrame(t)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open, placePopover])

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => {
      const t = e.target
      if (cardRef.current?.contains(t)) return
      if (popRef.current?.contains(t)) return
      onToggle(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') onToggle(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onToggle])

  return (
    <>
      <article
        ref={cardRef}
        className={`ph-card ph-card--${ind.status}${open ? ' ph-card--open' : ''} ph-card--clickable`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        title="Click para ver detalle del indicador"
        onClick={() => onToggle(!open)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle(!open)
          }
        }}
      >
        <div className="ph-card-top">
          <StatusDot status={ind.status} />
          <h3>{help.title}</h3>
          <span className={`ph-card-status ph-card-status--${ind.status}`}>
            {chip}
          </span>
        </div>
        <p className="ph-card-summary">{ind.summary}</p>
      </article>

      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              className="ph-card-pop"
              role="dialog"
              aria-label={help.title}
              style={{
                position: 'fixed',
                left: pos.left,
                width: pos.width,
                top: pos.top,
                bottom: pos.bottom,
                maxHeight: 'min(70vh, 520px)',
              }}
            >
              <div className="ph-card-pop-head">
                <strong>{help.title}</strong>
                <button
                  type="button"
                  className="ph-card-pop-close"
                  aria-label="Cerrar"
                  onClick={() => onToggle(false)}
                >
                  ×
                </button>
              </div>
              <div className="ph-card-pop-body">
                {help.rows.map((row) => (
                  <div key={row.label} className="ph-card-pop-row">
                    <span className="ph-card-pop-label">{row.label}</span>
                    <span className="ph-card-pop-value">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

function IndicatorGrid({ items, fetching, columnsClass, statusLabels }) {
  const [openId, setOpenId] = useState(null)

  if (!fetching && items.length === 0) {
    return <p className="ph-empty">Sin indicadores en esta área</p>
  }
  return (
    <div className={`ph-indicators ${columnsClass || ''}`}>
      {items.map((ind) => (
        <IndicatorCard
          key={ind.id}
          ind={ind}
          statusLabel={statusLabels?.[ind.id]}
          open={openId === ind.id}
          onToggle={(next) => setOpenId(next ? ind.id : null)}
        />
      ))}
    </div>
  )
}

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '—'
  const v = Number(n)
  if (v < 1024) return `${v} B`
  if (v < 1024 ** 2) return `${(v / 1024).toFixed(1)} KiB`
  if (v < 1024 ** 3) return `${(v / 1024 ** 2).toFixed(1)} MiB`
  return `${(v / 1024 ** 3).toFixed(2)} GiB`
}

function RabbitNodesTable({ rows, fetching }) {
  if (!fetching && (!rows || rows.length === 0)) {
    return (
      <p className="ph-empty">
        Sin nodos Rabbit (credenciales o /api/nodes)
      </p>
    )
  }
  return (
    <div className="ph-queue-block">
      <SectionTitle
        as="h3"
        className="ph-section-title--sub"
        info="Memoria Erlang del broker. mem_alarm=true bloquea publishers y tumba el portal."
      >
        Nodos broker · memoria
        {rows?.length ? ` · ${rows.length}` : ''}
      </SectionTitle>
      <div className="ph-table-wrap">
        <table className="ph-table">
          <thead>
            <tr>
              <th>Node</th>
              <th>Running</th>
              <th className="ph-num">Mem %</th>
              <th className="ph-num">Used</th>
              <th className="ph-num">Limit</th>
              <th>mem_alarm</th>
              <th>disk_alarm</th>
              <th>Status</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((n) => (
              <tr key={n.name} className={`ph-row--${n.status}`}>
                <td className="ph-name">{n.name}</td>
                <td>{n.running ? 'yes' : 'no'}</td>
                <td
                  className={`ph-num${
                    n.memPct != null && n.memPct >= 80 ? ' ph-num--hot' : ''
                  }`}
                >
                  {formatPct(n.memPct)}
                </td>
                <td className="ph-num">{formatBytes(n.memUsedBytes)}</td>
                <td className="ph-num">{formatBytes(n.memLimitBytes)}</td>
                <td>{n.memAlarm ? 'ALARM' : '—'}</td>
                <td>{n.diskAlarm ? 'ALARM' : '—'}</td>
                <td>
                  <StatusBadge status={n.status} />
                </td>
                <td className="ph-muted">{n.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ClusterNodesTable({ rows, fetching }) {
  if (!fetching && (!rows || rows.length === 0)) {
    return (
      <p className="ph-empty">
        Sin nodos EKS (RBAC ClusterRole nodes / metrics)
      </p>
    )
  }
  return (
    <div className="ph-queue-block">
      <SectionTitle
        as="h3"
        className="ph-section-title--sub"
        info="Mismo criterio que Lens/kubectl top: Ready + CPU/Mem vs allocatable. Warn ≥80%, Unavailable ≥95% o MemoryPressure."
      >
        Nodos AWS / EKS
        {rows?.length ? ` · ${rows.length}` : ''}
      </SectionTitle>
      <div className="ph-table-wrap">
        <table className="ph-table ph-table--nodes">
          <thead>
            <tr>
              <th>Name</th>
              <th>CPU</th>
              <th>Memory</th>
              <th className="ph-num">Taints</th>
              <th>Type</th>
              <th>Zone</th>
              <th>Age</th>
              <th>Conditions</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((n) => (
              <tr key={n.name} className={`ph-row--${n.status}`}>
                <td className="ph-name" title={n.name}>
                  {shortNodeName(n.name)}
                </td>
                <td>
                  <ResourceBar pct={n.cpuPct} kind="cpu" />
                </td>
                <td>
                  <ResourceBar pct={n.memPct} kind="mem" />
                </td>
                <td className="ph-num">{n.taints ?? 0}</td>
                <td className="ph-muted">{n.instanceType || '—'}</td>
                <td className="ph-muted">{n.zone || '—'}</td>
                <td className="ph-muted">{formatAge(n.createdAt)}</td>
                <td>
                  {n.ready ? (
                    <span className="ph-cond ph-cond--ready">Ready</span>
                  ) : (
                    <span className="ph-cond ph-cond--not-ready">NotReady</span>
                  )}
                </td>
                <td>
                  <StatusBadge status={n.status} />
                  {n.reason ? (
                    <span className="ph-muted ph-node-reason" title={n.reason}>
                      {n.reason}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function shortNodeName(name) {
  if (!name) return '—'
  // ip-172-30-0-100.us-east-2.compute.internal → ip-172-30-0-100
  const host = String(name).split('.')[0]
  return host || name
}

function formatAge(iso) {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.floor(sec / 60)}m`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`
  return `${Math.floor(sec / 86400)}d`
}

function ResourceBar({ pct, kind }) {
  if (pct == null || Number.isNaN(pct)) {
    return <span className="ph-muted">—</span>
  }
  const v = Math.min(100, Math.max(0, Number(pct)))
  const hot = v >= 80
  return (
    <div
      className={`ph-resbar ph-resbar--${kind}${hot ? ' ph-resbar--hot' : ''}`}
      title={`${v.toFixed(1)}%`}
    >
      <div className="ph-resbar-track">
        <div className="ph-resbar-fill" style={{ width: `${v}%` }} />
      </div>
      <span className="ph-resbar-label">{formatPct(pct)}</span>
    </div>
  )
}

function VultrInstancesTable({ rows, fetching, emptySummary }) {
  if (!fetching && (!rows || rows.length === 0)) {
    return (
      <div className="ph-queue-block">
        <SectionTitle
          as="h3"
          className="ph-section-title--sub"
          info="GET https://api.vultr.com/v2/instances. Solo lectura: no reinicia ni modifica instances."
        >
          Instances
        </SectionTitle>
        <p className="ph-empty">
          {emptySummary || 'Sin instances en la cuenta'}
        </p>
      </div>
    )
  }
  return (
    <div className="ph-queue-block">
      <SectionTitle
        as="h3"
        className="ph-section-title--sub"
        info="status es la suscripción (active, pending, suspended, resizing). power_status es running o stopped. server_status es ok, none, locked, installingbooting o isomounting."
      >
        Instances
        {rows?.length ? ` · ${rows.length}` : ''}
      </SectionTitle>
      <div className="ph-table-wrap">
        <table className="ph-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Hostname</th>
              <th>Region</th>
              <th>Plan</th>
              <th>Main IP</th>
              <th>Status</th>
              <th>Power status</th>
              <th>Server status</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((t) => (
              <tr key={t.id} className={`ph-row--${t.status}`}>
                <td className="ph-name" title={t.uso || t.os || ''}>
                  {t.label}
                  {t.uso ? (
                    <div className="ph-muted">{t.uso}</div>
                  ) : null}
                </td>
                <td className="ph-muted">{t.hostname || '—'}</td>
                <td>
                  <code>{t.region || '—'}</code>
                </td>
                <td className="ph-muted">{t.plan || '—'}</td>
                <td>
                  <code>{t.mainIp || '—'}</code>
                </td>
                <td>
                  <StatusBadge
                    status={t.instanceStatusHealth}
                    label={t.instanceStatus}
                  />
                </td>
                <td>
                  <StatusBadge
                    status={t.powerStatusHealth}
                    label={t.powerStatus}
                  />
                </td>
                <td>
                  <StatusBadge
                    status={t.serverStatusHealth}
                    label={t.serverStatus}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MongoSummaryStrip({ rows, indicator }) {
  const primary = (rows || []).find((r) => r.isPrimary) || (rows || [])[0]
  const cluster = primary?.clusterName || 'main'
  const rs = primary?.replicaSetName
  const warn = 80
  const error = 90
  return (
    <div className="ph-mongo-summary">
      <div className="ph-mongo-summary-card">
        <span className="ph-mongo-summary-label">Cluster</span>
        <span className="ph-mongo-summary-value">
          <code>{cluster}</code>
          {rs ? <span className="ph-muted"> · {rs}</span> : null}
        </span>
      </div>
      <div className="ph-mongo-summary-card">
        <span className="ph-mongo-summary-label">PRIMARY mem</span>
        <span
          className={`ph-mongo-summary-value${
            primary?.memPct != null && primary.memPct >= warn
              ? ' ph-num--hot'
              : ''
          }`}
        >
          {primary?.memPct != null ? `${primary.memPct}%` : '—'}
        </span>
        <span className="ph-muted">
          {primary?.memUsedBytes != null
            ? `${formatBytes(primary.memUsedBytes)} used`
            : ''}
          {primary?.memAvailBytes != null
            ? ` · ${formatBytes(primary.memAvailBytes)} avail`
            : ''}
        </span>
      </div>
      <div className="ph-mongo-summary-card">
        <span className="ph-mongo-summary-label">Conexiones PRIMARY</span>
        <span className="ph-mongo-summary-value">
          {primary?.connections != null
            ? Math.round(primary.connections)
            : '—'}
        </span>
      </div>
      <div className="ph-mongo-summary-card">
        <span className="ph-mongo-summary-label">CPU host PRIMARY</span>
        <span className="ph-mongo-summary-value">
          {primary?.cpuNormPct != null
            ? `${Math.round(primary.cpuNormPct)}%`
            : '—'}
        </span>
      </div>
      <div className="ph-mongo-summary-card">
        <span className="ph-mongo-summary-label">Umbrales mem</span>
        <span className="ph-mongo-summary-value ph-muted">
          Atención ≥{warn}% · Bloqueante ≥{error}%
        </span>
        {indicator?.summary ? (
          <span className="ph-muted" title={indicator.detail || ''}>
            {indicator.summary}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function MongoProcessesTable({ rows, fetching, emptySummary }) {
  if (!fetching && (!rows || rows.length === 0)) {
    return (
      <div className="ph-queue-block">
        <SectionTitle
          as="h3"
          className="ph-section-title--sub"
          info="GET Atlas …/processes + measurements (SYSTEM_MEMORY_*, CONNECTIONS, CPU). Solo lectura."
        >
          Procesos mongod
        </SectionTitle>
        <p className="ph-empty">
          {emptySummary ||
            'Sin procesos Atlas (faltan ATLAS_* o Metrics API no responde)'}
        </p>
      </div>
    )
  }
  return (
    <div className="ph-queue-block">
      <SectionTitle
        as="h3"
        className="ph-section-title--sub"
        info="Memoria de host = misma señal que alertas Atlas System Memory. CPU normalizada y conexiones son contexto operativo."
      >
        Procesos mongod
        {rows?.length ? ` · ${rows.length}` : ''}
      </SectionTitle>
      <div className="ph-table-wrap">
        <table className="ph-table">
          <thead>
            <tr>
              <th>Rol</th>
              <th>Host</th>
              <th>Memory</th>
              <th className="ph-num">Used</th>
              <th className="ph-num">Avail</th>
              <th className="ph-num">Conn</th>
              <th className="ph-num">CPU</th>
              <th>Status</th>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((p) => (
              <tr key={p.id} className={`ph-row--${p.status}`}>
                <td className="ph-name">
                  {p.isPrimary ? (
                    <strong>PRIMARY</strong>
                  ) : (
                    <span className="ph-muted">
                      {(p.typeName || 'SECONDARY').replace(/^REPLICA_/, '')}
                    </span>
                  )}
                </td>
                <td className="ph-muted" title={p.id}>
                  {p.hostname || p.id}
                </td>
                <td>
                  <ResourceBar pct={p.memPct} kind="mem" />
                </td>
                <td className="ph-num">{formatBytes(p.memUsedBytes)}</td>
                <td className="ph-num">{formatBytes(p.memAvailBytes)}</td>
                <td className="ph-num">
                  {p.connections != null ? Math.round(p.connections) : '—'}
                </td>
                <td className="ph-num">
                  {p.cpuNormPct != null
                    ? `${Math.round(p.cpuNormPct)}%`
                    : '—'}
                </td>
                <td>
                  <StatusBadge status={p.status} />
                </td>
                <td className="ph-muted">{p.summary || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function McpChainPanel({ chain, fetching }) {
  if (!fetching && !chain) return null
  const hops = chain?.hops || []
  return (
    <div className="ph-chain">
      <div className="ph-section-head">
        <SectionTitle
          as="h3"
          className="ph-section-title--sub"
          info="Hop 3 prueba INTERNAL_API_SECRET: si /api está OK y este falla, las tools MCP están rotas aunque el connector responda."
        >
          Cadena MCP → API → smart_back
        </SectionTitle>
        {chain ? <StatusBadge status={chain.status} /> : null}
      </div>
      {chain?.summary ? (
        <p className="ph-chain-summary">{chain.summary}</p>
      ) : fetching ? (
        <span className="ph-skel ph-skel--line" />
      ) : null}
      <ol className="ph-chain-hops">
        {hops.map((h, i) => (
          <li key={h.id} className={`ph-chain-hop ph-chain-hop--${h.status}`}>
            {i > 0 ? (
              <span className="ph-chain-arrow" aria-hidden>
                →
              </span>
            ) : null}
            <div className="ph-chain-hop-card">
              <StatusBadge status={h.status} />
              <strong>{h.label}</strong>
              <span className="ph-muted">{h.summary}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

function SiiBacklogPanel({ backlog, fetching }) {
  if (!fetching && !backlog) return null
  const queues = backlog?.queues || []
  return (
    <div className="ph-sii-backlog">
      <div className="ph-section-head">
        <SectionTitle
          as="h3"
          className="ph-section-title--sub"
          info="Colas get/poll (sii-*, tax-documents): Ready es el backlog. No alertan en «Colas clave»; aquí sí se ve el volumen."
        >
          Backlog SII · get/poll
        </SectionTitle>
        {backlog ? <StatusBadge status={backlog.status} /> : null}
      </div>
      {backlog ? (
        <p className="ph-queue-totals">
          Σ Ready <strong>{formatCount(backlog.totalReady)}</strong>
          {' · '}
          {backlog.summary}
        </p>
      ) : null}
      <div className="ph-table-wrap">
        <table className="ph-table">
          <thead>
            <tr>
              <th>Cola</th>
              <th>Modo</th>
              <th className="ph-num">Ready · backlog</th>
              <th className="ph-num">Unacked</th>
              <th className="ph-num">Total</th>
              <th>Alerta</th>
            </tr>
          </thead>
          <tbody>
            {queues.map((q) => (
              <tr key={q.name} className={`ph-row--${q.status}`}>
                <td className="ph-name">{q.name}</td>
                <td className="ph-muted">{q.consumeMode || '—'}</td>
                <td
                  className={`ph-num${
                    q.status !== 'ok'
                      ? ' ph-num--hot'
                      : q.ready > 0
                        ? ' ph-num--warm'
                        : ''
                  }`}
                >
                  {formatCount(q.ready)}
                </td>
                <td className="ph-num">{formatCount(q.unacked)}</td>
                <td className="ph-num">{formatCount(q.total)}</td>
                <td>
                  <StatusBadge
                    status={q.status}
                    label={
                      q.status === 'error'
                        ? 'Backlog alto'
                        : q.status === 'warn'
                          ? 'Backlog'
                          : 'OK'
                    }
                  />
                </td>
              </tr>
            ))}
            {!fetching && queues.length === 0 ? (
              <tr>
                <td colSpan={6} className="ph-empty">
                  Sin colas SII en el snapshot
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function shortPodHint(name) {
  if (!name) return '—'
  if (name.length <= 28) return name
  return `${name.slice(0, 12)}…${name.slice(-10)}`
}

function HeavyTrafficPanel({ traffic, fetching }) {
  if (!fetching && !traffic) return null
  const users = traffic?.users || []
  const byPod = traffic?.byPod || []
  const hot = traffic?.hotDeployments || []
  return (
    <div className="ph-heavy">
      <div className="ph-section-head">
        <SectionTitle
          as="h3"
          className="ph-section-title--sub"
          info="Usuarios: portal_request_logs (GraphQL) + apilogs (API pública). Pods: solo portal_request_logs (apilogs no trae podHostname). Ventana 1h. Cruza con deployments mem≥80%."
        >
          Tráfico pesado · usuarios / pods
        </SectionTitle>
        {traffic ? <StatusBadge status={traffic.status} /> : null}
      </div>
      {traffic?.summary ? (
        <p className="ph-muted ph-heavy-summary">{traffic.summary}</p>
      ) : null}

      {hot.length > 0 ? (
        <div className="ph-heavy-hot">
          <strong>Deployments con memoria alta</strong>
          <ul>
            {hot.map((d) => (
              <li key={d.deployment}>
                <StatusBadge status={d.expected ? 'ok' : d.status} />{' '}
                {d.deployment} · mem {formatPct(d.memPct)}
                {d.cpuPct != null ? ` · cpu ${formatPct(d.cpuPct)}` : ''}
                {d.expected ? (
                  <span className="ph-muted">
                    {' '}
                    · excepción esperada
                    {d.note ? (
                      <InfoTip text={d.note} />
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="ph-heavy-grid">
        <div className="ph-table-wrap">
          <table className="ph-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th className="ph-num">Req/1h</th>
                <th className="ph-num">5xx</th>
                <th>Origen</th>
                <th>Pods</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.userId}>
                  <td className="ph-name" title={u.userId}>
                    {u.email || `${u.userId.slice(0, 8)}…`}
                  </td>
                  <td
                    className={`ph-num${u.requests >= 50 ? ' ph-num--hot' : ''}`}
                  >
                    {formatCount(u.requests)}
                  </td>
                  <td className={`ph-num${u.s5xx > 0 ? ' ph-num--hot' : ''}`}>
                    {formatCount(u.s5xx)}
                  </td>
                  <td className="ph-muted">
                    {(u.origens || []).join(', ') || '—'}
                  </td>
                  <td className="ph-muted">
                    {(u.pods || []).length
                      ? u.pods.map(shortPodHint).join(', ')
                      : '—'}
                  </td>
                </tr>
              ))}
              {!fetching && users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="ph-empty">
                    Sin tráfico en api_logs (1h)
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="ph-table-wrap">
          <table className="ph-table">
            <thead>
              <tr>
                <th>Pod (HOSTNAME)</th>
                <th className="ph-num">Req/1h</th>
                <th className="ph-num">5xx</th>
              </tr>
            </thead>
            <tbody>
              {byPod.map((p) => (
                <tr key={p.podHostname}>
                  <td className="ph-name" title={p.podHostname}>
                    {shortPodHint(p.podHostname)}
                  </td>
                  <td className="ph-num">{formatCount(p.requests)}</td>
                  <td className={`ph-num${p.s5xx > 0 ? ' ph-num--hot' : ''}`}>
                    {formatCount(p.s5xx)}
                  </td>
                </tr>
              ))}
              {!fetching && byPod.length === 0 ? (
                <tr>
                  <td colSpan={3} className="ph-empty">
                    Sin podHostname en logs todavía
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const QUEUE_COLS = 11

/** Magnitud de colas Rabbit (Ready / Unacked / rates) — estilo Management UI. */
function QueueMetricsTable({ rows, fetching, areaLabel }) {
  const ordered = useMemo(() => sortQueuesByCriticality(rows), [rows])
  const totals = useMemo(() => {
    return ordered.reduce(
      (acc, q) => ({
        ready: acc.ready + (q.ready || 0),
        unacked: acc.unacked + (q.unacked || 0),
        total: acc.total + (q.total || 0),
        publishRate: acc.publishRate + (q.publishRate || 0),
        deliverRate: acc.deliverRate + (q.deliverRate || 0),
        ackRate: acc.ackRate + (q.ackRate || 0),
      }),
      {
        ready: 0,
        unacked: 0,
        total: 0,
        publishRate: 0,
        deliverRate: 0,
        ackRate: 0,
      },
    )
  }, [ordered])

  return (
    <div className="ph-queue-block">
      <div className="ph-section-head">
        <SectionTitle
          as="h3"
          className="ph-section-title--sub"
          info={
            <>
              <strong>Ready</strong> = msgs esperando (eso es el backlog).
              <strong> Alerta</strong> = OK / Backlog (≥ umbral en colas
              consume) / Sin consumer. Unacked = en proceso. Colas{' '}
              <code>get / poll</code> no alertan por Ready ni consumers=0.
              El nombre abre{' '}
              <a
                href="https://rabbit.smartcfo.cl/"
                target="_blank"
                rel="noopener noreferrer"
              >
                rabbit.smartcfo.cl
              </a>
              .
            </>
          }
        >
          Colas Rabbit{areaLabel ? ` · ${areaLabel}` : ''}
          {ordered.length ? ` · ${ordered.length}` : ''}
        </SectionTitle>
        {ordered.length > 0 ? (
          <p className="ph-queue-totals">
            Ready <strong>{formatCount(totals.ready)}</strong>
            {' · '}
            Unacked <strong>{formatCount(totals.unacked)}</strong>
            {' · '}
            Total <strong>{formatCount(totals.total)}</strong>
            {' · '}
            in {formatRate(totals.publishRate)}
            {' / '}
            out {formatRate(totals.deliverRate)}
            {' / '}
            ack {formatRate(totals.ackRate)}
          </p>
        ) : null}
      </div>
      <div className="ph-table-wrap">
        <table className="ph-table ph-table--queues">
          <thead>
            <tr className="ph-table-group">
              <th colSpan={5} />
              <th colSpan={3} className="ph-th-group">
                Messages
              </th>
              <th colSpan={3} className="ph-th-group">
                Message rates
              </th>
            </tr>
            <tr>
              <th>Name</th>
              <th>Node</th>
              <th>State</th>
              <th>Consumers</th>
              <th>Alerta</th>
              <th className="ph-num" title="Mensajes esperando consumer (backlog)">
                Ready · backlog
              </th>
              <th className="ph-num">Unacked</th>
              <th className="ph-num">Total</th>
              <th className="ph-num">incoming</th>
              <th className="ph-num">deliver / get</th>
              <th className="ph-num">ack</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((q) => (
              <tr key={q.name} className={`ph-row--${q.status}`}>
                <td className="ph-name">
                  <a
                    className="ph-name-link"
                    href={buildRabbitQueueUrl(q.name, q.vhost)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Abrir ${q.name} en Rabbit Management`}
                  >
                    {q.name}
                  </a>
                  <ConsumeModeTag mode={q.consumeMode} />
                </td>
                <td className="ph-muted" title={q.node || ''}>
                  {shortNode(q.node)}
                </td>
                <td>
                  <span
                    className={`ph-queue-state ph-queue-state--${
                      q.state === 'running' ? 'ok' : q.state === 'missing' ? 'warn' : 'unknown'
                    }`}
                  >
                    {q.state || '—'}
                  </span>
                </td>
                <td className="ph-num" title={q.consumeMode === 'get' ? GET_POLL_TIP : undefined}>
                  {q.consumeMode === 'get' ? (
                    <span className="ph-consumers-get">
                      {q.consumers}
                      <span className="ph-consumers-get-note">n/a</span>
                    </span>
                  ) : (
                    q.consumers
                  )}
                </td>
                <td>
                  <StatusBadge
                    status={
                      q.consumeMode === 'get' ? 'ok' : q.status
                    }
                    label={rabbitQueueStatusLabel(q)}
                  />
                </td>
                <td
                  className={`ph-num${
                    q.consumeMode !== 'get' &&
                    q.status === 'warn' &&
                    (q.ready || 0) > 0
                      ? ' ph-num--hot'
                      : q.ready > 0
                        ? ' ph-num--warm'
                        : ''
                  }`}
                  title={
                    q.consumeMode === 'get'
                      ? 'Ready visible; no genera alerta Backlog (get/poll)'
                      : q.status === 'warn' && (q.ready || 0) > 0
                        ? 'Backlog: Ready ≥ umbral (Atención)'
                        : 'Msgs listos para entregar'
                  }
                >
                  {formatCount(q.ready)}
                </td>
                <td
                  className={`ph-num${q.unacked > 0 ? ' ph-num--hot' : ''}`}
                >
                  {formatCount(q.unacked)}
                </td>
                <td className="ph-num ph-num--strong">{formatCount(q.total)}</td>
                <td className="ph-num ph-rate">{formatRate(q.publishRate)}</td>
                <td className="ph-num ph-rate">{formatRate(q.deliverRate)}</td>
                <td className="ph-num ph-rate">{formatRate(q.ackRate)}</td>
              </tr>
            ))}
            {!fetching && ordered.length === 0 ? (
              <tr>
                <td colSpan={QUEUE_COLS} className="ph-empty">
                  Sin snapshots de colas (configura RABBIT_USER / RABBIT_PASS o espera el próximo refresh)
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FleetTable({
  rows,
  fetching,
  onlyProblems,
  setOnlyProblems,
  selected,
  selectRow,
  titleSuffix,
  emptyLabel,
}) {
  return (
    <div className="ph-fleet-block">
      <div className="ph-section-head">
        <SectionTitle
          as="h3"
          className="ph-section-title--sub"
          info="Click en un deployment para ver pods, events y logs del pod (API K8s)."
        >
          Deployments{titleSuffix ? ` · ${titleSuffix}` : ''}
        </SectionTitle>
        <label className="ph-filter">
          <input
            type="checkbox"
            checked={onlyProblems}
            onChange={(e) => setOnlyProblems(e.target.checked)}
          />
          Solo NotReady / Unavailable
        </label>
      </div>
      <div className="ph-table-wrap">
        <table className="ph-table">
          <thead>
            <tr>
              <th>Deployment</th>
              <th>Namespace</th>
              <th>Ready</th>
              <th>Pending</th>
              <th>restarts</th>
              <th>CrashLoop</th>
              <th>OOM</th>
              <th>CPU</th>
              <th>Mem</th>
              <th>Status</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const active =
                selected &&
                selected.name === row.name &&
                selected.namespace === row.namespace
              const rowKey = `${row.namespace}/${row.name}`
              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className={`ph-row--${row.status}${active ? ' ph-row--selected' : ''} ph-row--clickable`}
                    onClick={() => selectRow(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        selectRow(row)
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={!!active}
                  >
                    <td className="ph-name">
                      <span className="ph-expand-caret" aria-hidden>
                        {active ? '▾' : '▸'}
                      </span>
                      <button
                        type="button"
                        className="ph-name-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          selectRow(row)
                        }}
                      >
                        {row.name}
                      </button>
                    </td>
                    <td>{row.namespace}</td>
                    <td>
                      {row.ready}/{row.desired}
                      {row.unavailable > 0 ? (
                        <span className="ph-muted">
                          {' '}
                          · unavailableReplicas {row.unavailable}
                        </span>
                      ) : null}
                    </td>
                    <td>{row.pending}</td>
                    <td>{row.restarts}</td>
                    <td>{row.crashLoopCount || 0}</td>
                    <td>{row.oomCount || 0}</td>
                    <td>{formatPct(row.cpuPct)}</td>
                    <td>{formatPct(row.memPct)}</td>
                    <td>
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="ph-reason">{row.reason || '—'}</td>
                  </tr>
                  {active ? (
                    <tr className="ph-row--expand">
                      <td colSpan={FLEET_COLS} className="ph-expand-cell">
                        <FleetDrilldown
                          name={row.name}
                          namespace={row.namespace}
                          embedded
                        />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              )
            })}
            {!fetching && rows.length === 0 ? (
              <tr>
                <td colSpan={FLEET_COLS} className="ph-empty">
                  {emptyLabel || 'Sin deployments en esta área'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function tabStatus(tab, byId, fleet) {
  const statuses = []
  for (const id of tab.indicatorIds || []) {
    if (byId[id]?.status) statuses.push(byId[id].status)
  }
  const fleetRows = fleet.filter((f) => (tab.fleetNames || []).includes(f.name))
  for (const f of fleetRows) statuses.push(f.status)
  if (statuses.length === 0) return 'unknown'
  return worstStatus(statuses)
}

export function PortalHealth() {
  const [tab, setTab] = useState('resumen')
  const [onlyProblems, setOnlyProblems] = useState(false)
  const [selected, setSelected] = useState(null)
  const [result, reexecute] = useQuery({
    query: GET_PORTAL_HEALTH,
    requestPolicy: 'network-only',
  })
  const [surfaceResult, reexecuteSurface] = useQuery({
    query: GET_PORTAL_HEALTH_SURFACE,
    requestPolicy: 'network-only',
  })

  const refresh = useCallback(() => {
    reexecute({ requestPolicy: 'network-only' })
    reexecuteSurface({ requestPolicy: 'network-only' })
  }, [reexecute, reexecuteSurface])

  const refreshSurface = useCallback(() => {
    reexecuteSurface({ requestPolicy: 'network-only' })
  }, [reexecuteSurface])

  useEffect(() => {
    const id = window.setInterval(refreshSurface, POLL_MS)
    return () => window.clearInterval(id)
  }, [refreshSurface])

  useEffect(() => {
    const id = window.setInterval(() => {
      reexecute({ requestPolicy: 'network-only' })
    }, FULL_POLL_MS)
    return () => window.clearInterval(id)
  }, [reexecute])

  const full = result.data?.portalHealth
  const surface = surfaceResult.data?.portalHealthSurface
  /**
   * Hero / go-no-go: superficie (ready+shell+GraphQL).
   * No mezclar `unknown` del inventario pesado (Vultr/Atlas timeout) — eso
   * dejaba el hero en "Unknown / Consultando…" aunque el portal estuviera OK.
   * Inventario completo sigue en tabs; overall de tabs = peor entre
   * superficie y full, ignorando unknown del full si superficie está ok|warn|error.
   */
  const heroOverall = surface?.overall ?? full?.overall ?? null
  const tabOverall = (() => {
    if (!full && !surface) return null
    if (!full) return surface.overall
    if (!surface) return full.overall
    const fullEffective =
      full.overall === 'unknown' && surface.overall !== 'unknown'
        ? surface.overall
        : full.overall
    return worstStatus([surface.overall || 'ok', fullEffective || 'ok'])
  })()

  const data = full
    ? {
        ...full,
        overall: tabOverall || full.overall,
        summary:
          surface && surface.portalUp === false
            ? surface.summary
            : full.summary,
        checkedAt: surface?.checkedAt ?? full.checkedAt,
        uptime: surface?.uptime ?? full.uptime,
        indicators: mergeSurfaceIndicators(
          full.indicators || [],
          surface?.indicators || [],
        ),
      }
    : surface
      ? {
          checkedAt: surface.checkedAt,
          overall: surface.overall,
          summary: surface.summary,
          indicators: surface.indicators || [],
          fleet: [],
          nodes: [],
          rabbitQueues: [],
          rabbitNodes: [],
          vultrInstances: [],
          mongoProcesses: [],
          uptime: surface.uptime,
        }
      : null
  const fleet = data?.fleet || []
  const indicators = data?.indicators || []

  const byId = useMemo(() => {
    const map = {}
    for (const ind of indicators) map[ind.id] = ind
    return map
  }, [indicators])

  const activeTab = TABS.find((t) => t.id === tab) || TABS[0]

  useEffect(() => {
    if (!TABS.some((t) => t.id === tab)) setTab('resumen')
  }, [tab])

  const tabIndicators = useMemo(() => {
    if (!activeTab.indicatorIds) return []
    return activeTab.indicatorIds.map((id) => byId[id]).filter(Boolean)
  }, [activeTab, byId])

  const tabFleet = useMemo(() => {
    if (activeTab.id === 'resumen') return fleet.filter((f) => f.tier === 'critical')
    if (!activeTab.fleetNames?.length) return []
    return fleet.filter((f) => activeTab.fleetNames.includes(f.name))
  }, [activeTab, fleet])

  const otherFleet = useMemo(
    () => fleet.filter((f) => f.tier === 'other'),
    [fleet],
  )

  const showQueues =
    activeTab.id === 'rabbit' ||
    activeTab.id === 'mailing' ||
    activeTab.id === 'sii' ||
    activeTab.id === 'documents'

  const tabQueues = useMemo(() => {
    if (!showQueues) return []
    const queues = data?.rabbitQueues || []
    return queues.filter(
      (q) => Array.isArray(q.areas) && q.areas.includes(activeTab.id),
    )
  }, [showQueues, data?.rabbitQueues, activeTab.id])

  const tabFleetKey = tabFleet.map((f) => `${f.namespace}/${f.name}`).join('|')

  // Acceso rápido: al entrar a un worker clave, abrir el drill-down.
  useEffect(() => {
    if (activeTab.autoExpand && tabFleet.length > 0) {
      const row = tabFleet[0]
      setSelected({ name: row.name, namespace: row.namespace })
    } else if (!activeTab.autoExpand) {
      setSelected(null)
    }
  }, [activeTab.id, activeTab.autoExpand, tabFleetKey])
  const problemFleet = useMemo(
    () =>
      tabFleet.filter(
        (f) =>
          f.status === 'error' ||
          f.status === 'warn' ||
          f.status === 'unknown',
      ),
    [tabFleet],
  )
  const unavailableFleet = useMemo(
    () => tabFleet.filter((f) => f.status === 'error'),
    [tabFleet],
  )
  const notReadyFleet = useMemo(
    () => tabFleet.filter((f) => f.status === 'warn'),
    [tabFleet],
  )

  const visibleFleet = onlyProblems ? problemFleet : tabFleet

  const otherProblemFleet = useMemo(
    () =>
      otherFleet.filter(
        (f) =>
          f.status === 'error' ||
          f.status === 'warn' ||
          f.status === 'unknown',
      ),
    [otherFleet],
  )
  const visibleOtherFleet = onlyProblems ? otherProblemFleet : otherFleet

  const selectRow = useCallback((row) => {
    setSelected((prev) =>
      prev && prev.name === row.name && prev.namespace === row.namespace
        ? null
        : { name: row.name, namespace: row.namespace },
    )
  }, [])

  const areaCards = useMemo(
    () =>
      TABS.filter((t) => t.id !== 'resumen').map((t) => ({
        ...t,
        status: tabStatus(t, byId, fleet),
      })),
    [byId, fleet],
  )

  const checklistRows = useMemo(
    () =>
      buildChecklistRows(
        byId,
        fleet,
        data?.rabbitQueues,
        data?.nodes,
        data?.vultrInstances,
      ),
    [byId, fleet, data?.rabbitQueues, data?.nodes, data?.vultrInstances],
  )

  const vultrStatusLabels = useMemo(
    () => ({ vultr_ops: vultrHeadline(data?.vultrInstances) || 'unknown' }),
    [data?.vultrInstances],
  )

  const checklistBlocking = useMemo(
    () => checklistRows.filter((r) => r.status === 'error').length,
    [checklistRows],
  )
  const checklistAttention = useMemo(
    () => checklistRows.filter((r) => r.status === 'warn').length,
    [checklistRows],
  )
  const hasChecklistAlerts =
    checklistBlocking > 0 || checklistAttention > 0

  const indicatorCols =
    tabIndicators.length <= 1
      ? 'ph-indicators--1'
      : tabIndicators.length <= 3
        ? 'ph-indicators--3'
        : tabIndicators.length <= 5
          ? 'ph-indicators--5'
          : 'ph-indicators--8'

  return (
    <div className="ph-page">
      <header className="ph-header">
        <div>
          <p className="ph-kicker">Operaciones</p>
          <h1 className="ph-title">Salud del portal</h1>
          <p className="ph-subtitle">
            {tab === 'resumen'
              ? 'Checklist matutino del portal'
              : data?.summary ||
                (result.fetching || surfaceResult.fetching
                  ? 'Consultando…'
                  : 'Sin datos')}
          </p>
        </div>
        <div className="ph-header-actions">
          {tab !== 'resumen' && data ? (
            <StatusBadge
              status={data.overall}
              label={morningGoLabel(data.overall)}
            />
          ) : null}
          {tab !== 'resumen' ? (
            <span className="ph-checked">
              Actualizado {formatTime(data?.checkedAt)}
            </span>
          ) : null}
          <button
            type="button"
            className="ph-btn"
            onClick={refresh}
            disabled={result.fetching || surfaceResult.fetching}
          >
            {result.fetching || surfaceResult.fetching ? 'Refrescando…' : 'Refrescar'}
          </button>
        </div>
      </header>

      {result.error ? (
        <div className="ph-error" role="alert">
          {result.error.message || 'No se pudo cargar la salud del portal'}
        </div>
      ) : null}

      <nav className="ph-tabs" aria-label="Áreas del portal">
        {TAB_GROUPS.map((group) => (
          <div
            key={group.id}
            className="ph-tab-group"
            role="group"
            aria-label={group.label || 'Resumen'}
          >
            {group.label ? (
              <span className="ph-tab-group-label">{group.label}</span>
            ) : null}
            <div className="ph-tab-group-tabs" role="tablist">
              {group.tabIds.map((id) => {
                const t = TABS_BY_ID[id]
                if (!t) return null
                const status =
                  t.id === 'resumen' ? data?.overall : tabStatus(t, byId, fleet)
                const active = tab === t.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`ph-tab${active ? ' ph-tab--active' : ''}`}
                    onClick={() => setTab(t.id)}
                  >
                    <StatusDot status={status || 'unknown'} />
                    <span>{t.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {tab !== 'resumen' ? (
        <p className="ph-tab-hint">{activeTab.hint}</p>
      ) : null}

      {tab !== 'resumen' && unavailableFleet.length > 0 ? (
        <div className="ph-fail-banner">
          <strong>
            {unavailableFleet.length}/{tabFleet.length} Unavailable:
          </strong>{' '}
          {unavailableFleet.map((f) => f.name).join(', ')}
        </div>
      ) : null}
      {tab !== 'resumen' &&
      unavailableFleet.length === 0 &&
      notReadyFleet.length > 0 ? (
        <div className="ph-warn-banner">
          <strong>
            {notReadyFleet.length}/{tabFleet.length} NotReady (degradado):
          </strong>{' '}
          {notReadyFleet.map((f) => f.name).join(', ')}
        </div>
      ) : null}

      {tab === 'resumen' ? (
        <>
          <section className="ph-section ph-section--resumen">
            <ResumenHero
              overall={heroOverall || data?.overall}
              blocking={checklistBlocking}
              attention={checklistAttention}
              uptime={data?.uptime}
              checkedAt={data?.checkedAt}
              loading={
                !data &&
                (result.fetching || surfaceResult.fetching)
              }
            />

            {hasChecklistAlerts ? (
              <AffectedItemsPanel
                rows={checklistRows}
                onOpenTab={(id) => {
                  if (id && id !== 'resumen') setTab(id)
                }}
              />
            ) : null}

            <div className="ph-resumen-blocks">
              <div className="ph-resumen-blocks-head">
                <h2 className="ph-section-title">Bloques críticos</h2>
                <span className="ph-resumen-blocks-hint">
                  Click para abrir el detalle
                </span>
              </div>
              <BlockStatusGrid
                rows={checklistRows}
                fetching={result.fetching}
                onOpenTab={(id) => {
                  if (id && id !== 'resumen') setTab(id)
                }}
              />
            </div>
          </section>

          <section className="ph-section">
            <div className="ph-resumen-blocks-head ph-resumen-blocks-head--stack">
              <h2 className="ph-section-title">
                Disponibilidad de probes del portal
              </h2>
              <p className="ph-section-sub">
                Porcentaje de checks (~2 min) en los que ningún probe crítico del
                portal estuvo en error.
              </p>
            </div>
            <div className="ph-uptime-card">
              {data?.uptime ? (
                <PortalUptimeChart uptime={data.uptime} />
              ) : (
                <p className="ph-empty">
                  {result.fetching
                    ? 'Cargando historial de probes del portal…'
                    : 'Sin historial de probes del portal'}
                </p>
              )}
            </div>
          </section>

          <section className="ph-section">
            <HeavyTrafficPanel
              traffic={data?.heavyTraffic}
              fetching={result.fetching}
            />
          </section>

          <details className="ph-more-details" open={!hasChecklistAlerts}>
            <summary>
              Flota y áreas
              {hasChecklistAlerts ? ' · secundario' : ''}
            </summary>

          <section className="ph-section">
            <SectionTitle info="Click en un área para ver probes, colas y deployments.">
              Áreas
            </SectionTitle>
            <div className="ph-area-grid">
              {areaCards.map((area) => (
                <button
                  key={area.id}
                  type="button"
                  className={`ph-area-card ph-area-card--${area.status}`}
                  onClick={() => setTab(area.id)}
                >
                  <div className="ph-area-card-top">
                    <StatusDot status={area.status} />
                    <strong>{area.label}</strong>
                    {area.hint ? <InfoTip text={area.hint} /> : null}
                    <span
                      className={`ph-card-status ph-card-status--${area.status}`}
                    >
                      {morningLabel(area.status)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="ph-section">
            {unavailableFleet.length > 0 ? (
              <div className="ph-fail-banner">
                <strong>
                  {unavailableFleet.length}/{tabFleet.length} Unavailable:
                </strong>{' '}
                {unavailableFleet.map((f) => f.name).join(', ')}
              </div>
            ) : notReadyFleet.length > 0 ? (
              <div className="ph-warn-banner">
                <strong>
                  {notReadyFleet.length}/{tabFleet.length} NotReady (degradado):
                </strong>{' '}
                {notReadyFleet.map((f) => f.name).join(', ')}
              </div>
            ) : null}
            <SectionTitle info="Entrada, broker, workers del portal, APIs y admin. Afectan el overall.">
              Flota crítica
            </SectionTitle>
            <FleetTable
              rows={visibleFleet}
              fetching={result.fetching}
              onlyProblems={onlyProblems}
              setOnlyProblems={setOnlyProblems}
              selected={selected}
              selectRow={selectRow}
              titleSuffix={`${tabFleet.length}${
                problemFleet.length
                  ? ` · ${problemFleet.length} NotReady/Unavailable`
                  : ' · Ready'
              }`}
              emptyLabel={
                onlyProblems
                  ? 'Ningún deployment NotReady/Unavailable'
                  : 'Sin datos de flota crítica'
              }
            />
          </section>

          <section className="ph-section">
            <SectionTitle info="Workers con desired>0 en prod. No contaminan el overall del portal.">
              Otros (prod Running)
            </SectionTitle>
            <FleetTable
              rows={visibleOtherFleet}
              fetching={result.fetching}
              onlyProblems={onlyProblems}
              setOnlyProblems={setOnlyProblems}
              selected={selected}
              selectRow={selectRow}
              titleSuffix={`${otherFleet.length}${
                otherProblemFleet.length
                  ? ` · ${otherProblemFleet.length} con alerta`
                  : ' · Ready'
              }`}
              emptyLabel={
                onlyProblems
                  ? 'Ningún deployment con alerta en Otros'
                  : 'Sin workers en Otros'
              }
            />
          </section>
          </details>
        </>
      ) : (
        <section className="ph-section">
          {activeTab.indicatorIds ? (
            <>
              <SectionTitle info="Click en una tarjeta para ver qué mide y cuándo alerta.">
                Indicadores
              </SectionTitle>
              <IndicatorGrid
                items={tabIndicators}
                fetching={result.fetching}
                columnsClass={indicatorCols}
                statusLabels={vultrStatusLabels}
              />
            </>
          ) : null}

          {activeTab.id === 'mcp' ? (
            <div className="ph-tab-queues">
              <McpChainPanel
                chain={data?.mcpChain}
                fetching={result.fetching}
              />
            </div>
          ) : null}

          {activeTab.id === 'sii' ? (
            <div className="ph-tab-queues">
              <SiiBacklogPanel
                backlog={data?.siiBacklog}
                fetching={result.fetching}
              />
            </div>
          ) : null}

          {showQueues ? (
            <div className={activeTab.indicatorIds || activeTab.fleetNames ? 'ph-tab-queues' : undefined}>
              <QueueMetricsTable
                rows={tabQueues}
                fetching={result.fetching}
                areaLabel={activeTab.label}
              />
            </div>
          ) : null}

          {activeTab.id === 'rabbit' ? (
            <div className="ph-tab-queues">
              <RabbitNodesTable
                rows={data?.rabbitNodes}
                fetching={result.fetching}
              />
            </div>
          ) : null}

          {activeTab.id === 'nodes' ? (
            <div className="ph-tab-queues">
              <ClusterNodesTable
                rows={data?.nodes}
                fetching={result.fetching}
              />
            </div>
          ) : null}

          {activeTab.id === 'vultr' ? (
            <div className="ph-tab-queues">
              <VultrInstancesTable
                rows={data?.vultrInstances}
                fetching={result.fetching}
                emptySummary={byId.vultr_ops?.summary}
              />
            </div>
          ) : null}

          {activeTab.id === 'mongo' ? (
            <div className="ph-tab-queues">
              <MongoSummaryStrip
                rows={data?.mongoProcesses}
                indicator={byId.mongo_memory}
              />
              <MongoProcessesTable
                rows={data?.mongoProcesses}
                fetching={result.fetching}
                emptySummary={byId.mongo_memory?.summary}
              />
            </div>
          ) : null}

          {(activeTab.fleetNames?.length) ? (
            <div className={activeTab.indicatorIds || showQueues ? 'ph-tab-fleet' : undefined}>
              <FleetTable
                rows={visibleFleet}
                fetching={result.fetching}
                onlyProblems={onlyProblems}
                setOnlyProblems={setOnlyProblems}
                selected={selected}
                selectRow={selectRow}
                titleSuffix={`${tabFleet.length}`}
                emptyLabel={
                  onlyProblems
                    ? 'Ningún deployment NotReady/Unavailable'
                    : 'Sin deployments en esta área'
                }
              />
            </div>
          ) : null}

          {activeTab.id === 'front' ? (
            <aside className="ph-front-panel">
              <h3 className="ph-front-panel-title">Qué medimos en Front</h3>
              <ul className="ph-front-panel-list">
                <li>
                  <strong>Shell /signin y /</strong> — CloudFront entrega el HTML del SPA
                  (<a href="https://portal.smartcfo.cl/signin" target="_blank" rel="noopener noreferrer">
                    portal.smartcfo.cl/signin
                  </a>
                  ). Solo esto no basta: el login y el resto de clicks no pasan por esta URL.
                </li>
                <li>
                  <strong>Assets JS/CSS</strong> — bundles <code>/static/js</code> y{' '}
                  <code>/static/css</code>. Si el HTML carga y el JS no, la pantalla queda en blanco.
                </li>
                <li>
                  <strong>GraphQL (SPA)</strong> — <code>server.smartcfo.cl/graphql</code>, backend
                  real de login, mutaciones y queries. La mayoría de interacciones fallidas del
                  portal se ven aquí (o en logs de <code>smart-tech-api</code>), no en el HTML estático.
                </li>
              </ul>
              <p className="ph-front-panel-limit">
                Limitación: no hay telemetría en el browser (exception de JS en un click concreto).
                Para errores de requests en vivo, abre los logs del monolito.
              </p>
              <LogLinks job="smart-tech-api" />
            </aside>
          ) : null}

          {activeTab.id === 'rabbit' ? (
            <aside className="ph-front-panel">
              <h3 className="ph-front-panel-title">RabbitMQ (StatefulSet)</h3>
              <ul className="ph-front-panel-list">
                <li>
                  <strong>Pods</strong> — StatefulSet <code>rabbitmq-ha</code> (no es
                  Deployment). Expandido abajo: ready/restarts/events.
                </li>
                <li>
                  <strong>Management UI</strong> —{' '}
                  <a
                    href="https://rabbit.smartcfo.cl/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    rabbit.smartcfo.cl
                  </a>{' '}
                  (HTML del panel, sin login).
                </li>
                <li>
                  <strong>API + colas</strong> — tabla Ready / Unacked / rates abajo
                  (email, SII, documents y banking). Requiere{' '}
                  <code>RABBIT_USER</code> / <code>RABBIT_PASS</code> en
                  server-admin (mismas del Management).
                </li>
                <li>
                  <strong>Memoria nodos</strong> — <code>/api/nodes</code>: si{' '}
                  <code>mem_alarm</code> se activa, Rabbit bloquea publishers y el
                  portal deja de encolar (mailing, SII, documents). Alerta desde 80%;
                  error desde 90% o con alarm.
                </li>
              </ul>
              <a
                className="ph-btn ph-btn-link"
                href="https://rabbit.smartcfo.cl/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Management UI
              </a>
            </aside>
          ) : null}

          {activeTab.id === 'nodes' ? (
            <aside className="ph-front-panel">
              <h3 className="ph-front-panel-title">Nodos EKS (AWS)</h3>
              <ul className="ph-front-panel-list">
                <li>
                  <strong>Ready / Pressure</strong> — condiciones del Node: Ready,
                  MemoryPressure, DiskPressure, PIDPressure.
                </li>
                <li>
                  <strong>CPU / Mem %</strong> — usage (metrics-server) vs allocatable.
                  Warn ≥80%, Unavailable ≥95%.
                </li>
                <li>
                  Requiere <code>kubectl apply -f k8s/health-rbac.yaml</code> (ClusterRole
                  nodes + metrics.k8s.io/nodes).
                </li>
              </ul>
            </aside>
          ) : null}

          {activeTab.id === 'vultr' ? (
            <aside className="ph-front-panel">
              <h3 className="ph-front-panel-title">Instances</h3>
              <ul className="ph-front-panel-list">
                <li>
                  <strong>Status</strong> — suscripción de la instance:{' '}
                  <code>active</code>, <code>pending</code>, <code>suspended</code>,{' '}
                  <code>resizing</code>.
                </li>
                <li>
                  <strong>Power status</strong> — <code>running</code> o{' '}
                  <code>stopped</code>.
                </li>
                <li>
                  <strong>Server status</strong> — <code>ok</code>, <code>none</code>,{' '}
                  <code>locked</code>, <code>installingbooting</code>,{' '}
                  <code>isomounting</code>.
                </li>
                <li>
                  Consulta <code>GET /v2/instances</code> con <code>VULTR_API_KEY</code>.
                  No crea, no reinicia y no modifica instances. Si falta una IP
                  operativa (SII Squid, Capitaria, exit Chile), el indicador lo marca.
                </li>
              </ul>
            </aside>
          ) : null}

          {activeTab.id === 'mongo' ? (
            <aside className="ph-front-panel">
              <h3 className="ph-front-panel-title">Cómo leer esta pestaña</h3>
              <ul className="ph-front-panel-list">
                <li>
                  <strong>PRIMARY mem</strong> — System Memory del host Atlas.
                  Es la misma métrica que dispara las alertas de alto uso de RAM.
                  Atención ≥80%, Bloqueante ≥90%.
                </li>
                <li>
                  <strong>Tabla de procesos</strong> — cada nodo del replica set
                  (PRIMARY + SECONDARY) con memoria used/avail, conexiones abiertas
                  y CPU normalizada del host.
                </li>
                <li>
                  <strong>Qué no es</strong> — no sustituye el ping de{' '}
                  <code>smart_back ready</code> (pestaña Server). Aquí ves presión
                  de capacidad; allí ves si el monolito responde.
                </li>
                <li>
                  Datos vía Atlas Metrics API (solo lectura). Cluster{' '}
                  <code>main</code> / proyecto <code>core_cl</code>.
                </li>
              </ul>
              <a
                className="ph-btn ph-btn-link"
                href="https://cloud.mongodb.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir Atlas
              </a>
            </aside>
          ) : null}

          {activeTab.relatedQueues ? (
            <aside className="ph-front-panel">
              <h3 className="ph-front-panel-title">
                Cómo ver por qué falla {activeTab.label}
              </h3>
              <ul className="ph-front-panel-list">
                <li>
                  El detalle del deployment ya está expandido: pods, events K8s y causa probable.
                </li>
                <li>
                  Magnitud de colas arriba (Ready / Unacked / rates). Detalle en Management:{' '}
                  {activeTab.relatedQueues.map((name, i) => (
                    <span key={name}>
                      {i > 0 ? ', ' : null}
                      <a
                        href={buildRabbitQueueUrl(name)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {name}
                      </a>
                    </span>
                  ))}
                  {' · '}
                  <button
                    type="button"
                    className="ph-link-btn"
                    onClick={() => setTab('rabbit')}
                  >
                    Ver todas en Rabbit
                  </button>
                </li>
              </ul>
              {activeTab.fleetNames?.[0] ? (
                <LogLinks job={activeTab.fleetNames[0]} />
              ) : null}
            </aside>
          ) : null}
        </section>
      )}
    </div>
  )
}

export default PortalHealth
